import { Fragment } from 'react';
import { Phone } from 'lucide-react';
import { formatPhone } from '@meshsmith/shared';
import { classNames } from '../lib/format';
import { useSupport } from '../hooks/useSupport';

/**
 * "Call us" with every support number as a tap-to-call link, and the opening hours.
 * `inline` is one line for the bar at the top of the page; `stacked` is for menus and the footer.
 */
export function CallUs({
  variant = 'stacked',
  className,
}: {
  variant?: 'inline' | 'stacked';
  className?: string;
}) {
  const { phones, hours } = useSupport();
  if (phones.length === 0) return null;

  const numbers = phones.map((p, i) => (
    <Fragment key={p}>
      {i > 0 && (
        <span
          aria-hidden
          className={variant === 'inline' ? 'text-white/40' : 'text-secondary-grey/50'}
        >
          ·
        </span>
      )}
      <a
        href={`tel:${p}`}
        className={classNames(
          'whitespace-nowrap font-medium hover:underline hover:underline-offset-2',
          variant === 'inline' ? 'hover:text-white' : 'hover:text-deep-teal',
        )}
      >
        {formatPhone(p)}
      </a>
    </Fragment>
  ));

  if (variant === 'inline') {
    return (
      <p className={classNames('flex items-center gap-1.5 whitespace-nowrap', className)}>
        <Phone size={12} aria-hidden className="shrink-0 text-logo-from" />
        <span className="sr-only">Call us:</span>
        {numbers}
        {hours && <span className="ml-1 text-white/60">({hours})</span>}
      </p>
    );
  }

  return (
    <div className={classNames('text-sm', className)}>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-secondary-grey">
        <Phone size={13} aria-hidden /> Call us
      </p>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">{numbers}</p>
      {hours && <p className="mt-0.5 text-xs text-secondary-grey">{hours}</p>}
    </div>
  );
}
